import { AppsOrder } from "@zapier/bff";
import {
  useCurrentUser,
  useApps,
  sortApps,
  sortAppsByTitle,
  getNextAppSortState,
  AppSortState,
} from "@zapier/client";
import { AppSearchInteractionOption, useRouterParams } from "@zapier/shared";
import { SortDescriptor } from "@zapier/design-system";
import { APPS_SORT_LIMIT } from "@zapier/utils";
import { useEffect, useState, useMemo, useCallback } from "react";

interface Input {
  pageSize: number;
  /**
   * When true (APCON-53 redesigned filter bar), no `shared` param means the
   * Version chip was cleared, so the list merges public and private apps.
   * The legacy path keeps its old fallback because its Sharing dropdown
   * always displays "Latest public app versions" when `shared` is unset.
   */
  isRedesignEnabled?: boolean;
}

export function useAppsList({ pageSize, isRedesignEnabled = false }: Input) {
  // Read query params from URL when component mounts
  const {
    order,
    owner: selectedOwner,
    page: urlPage,
    search,
    setParams,
    shared: selectedShared,
  } = useRouterParams();

  const { isLoading: isUserLoading } = useCurrentUser();

  /** Sort state for frontend sorting **************************************** */

  const [sortState, setSortState] = useState<AppSortState>({
    field: undefined,
    direction: "asc",
  });

  /** Shared filtering selection **************************************** */

  // In the redesigned view, no `shared` param means no Version chip is
  // applied, so show public and private apps merged — otherwise private apps
  // would be silently hidden from a list that looks unfiltered (CRIT-3021).
  // An explicit value (and the legacy view) keeps the single-source behavior.
  const filterType: "latest_public" | "other" | "all" =
    !selectedShared && isRedesignEnabled
      ? "all"
      : selectedShared === AppSearchInteractionOption.Other
        ? AppSearchInteractionOption.Other
        : AppSearchInteractionOption.LatestPublic;

  /** Fetch apps *********************************************** */

  // The merged (unfiltered) view combines two server-paginated sources, so —
  // like column sorting — it fetches the full dataset and paginates on the
  // client.
  const isMergedView = filterType === "all";
  const needsFullDataset = !!sortState.field || isMergedView;

  // Use larger page size when sorting or merging to fetch all apps
  const effectivePageSize = needsFullDataset ? APPS_SORT_LIMIT : pageSize;

  // When sorting or merging is active, always fetch page 1 to get all apps
  const effectivePage = needsFullDataset ? 1 : urlPage;

  const {
    hydratedApps,
    isFetching,
    isLoading: isUseAppsLoading,
    totalPages: backendTotalPages,
    totalCount: backendTotalCount,
  } = useApps({
    filterType,
    order: sortState.field
      ? undefined // Ignore URL order param during client-side sorting to keep query key stable
      : order
        ? (order as AppsOrder)
        : undefined,
    page: effectivePage,
    pageSize: effectivePageSize,
    search: search || undefined,
  });

  /** Sorting logic ********************************************************* */

  const isSortDisabledDueToLimit = (backendTotalCount || 0) > APPS_SORT_LIMIT;
  const isSortEnabled = (backendTotalCount || 0) <= APPS_SORT_LIMIT;

  // Check if we have the full dataset needed for sorting
  // Must not be fetching AND have all apps loaded
  const hasFullDataset =
    !isFetching &&
    hydratedApps &&
    hydratedApps.length >= (backendTotalCount || 0);

  // Sort apps on the frontend when sorting is enabled AND we have the full dataset
  const sortedApps = useMemo(() => {
    if (sortState.field && isSortEnabled) {
      // Don't sort partial data - wait for full dataset (return empty array to prevent map errors)
      if (!hydratedApps || !hasFullDataset) {
        return [];
      }
      return sortApps(hydratedApps, sortState.field, sortState.direction);
    }
    if (isMergedView) {
      // Don't sort partial data - wait for full dataset (mirrors the sort branch)
      if (!hydratedApps || !hasFullDataset) {
        return [];
      }
      // Public and private apps arrive as two separately-sorted lists;
      // re-sort the merged list so pagination slices stay alphabetical
      // across both sources.
      return sortAppsByTitle(hydratedApps, order === "-title" ? "desc" : "asc");
    }
    return hydratedApps;
  }, [
    hydratedApps,
    sortState.field,
    sortState.direction,
    isSortEnabled,
    hasFullDataset,
    isMergedView,
    order,
  ]);

  /** Frontend pagination for sorted results ******************************** */

  const sortTotalPages = sortedApps?.length
    ? Math.ceil(sortedApps.length / pageSize)
    : 1;
  const sortTotalCount = sortedApps?.length || 0;

  // Use URL page for sorted pagination (Pagination component updates URL on click)
  const sortPage = urlPage || 1;

  const paginatedApps = useMemo(() => {
    if (!needsFullDataset || !sortedApps) {
      return sortedApps;
    }
    return sortedApps.slice((sortPage - 1) * pageSize, sortPage * pageSize);
  }, [needsFullDataset, sortedApps, sortPage, pageSize]);

  // Use paginated apps when sorting or merging, raw apps otherwise
  const apps = needsFullDataset ? paginatedApps : sortedApps;

  /** Sort change handler *************************************************** */

  const onSortChange = useCallback(
    (sortDescriptor: SortDescriptor) => {
      const nextState = getNextAppSortState(sortState, sortDescriptor);
      setSortState(nextState);
      // Reset to page 1 when sort changes (matching Folders pattern)
      setParams({ page: 1 });
    },
    [sortState, setParams],
  );

  // Build sort descriptor for table from current sort state
  const sortDescriptor: SortDescriptor | undefined = sortState.field
    ? {
        column: sortState.field,
        direction: sortState.direction === "asc" ? "ascending" : "descending",
      }
    : undefined;

  /** Determine which pagination values to use ****************************** */

  const paginationPage = needsFullDataset ? sortPage : urlPage;
  const paginationTotalPages = needsFullDataset
    ? sortTotalPages
    : backendTotalPages;
  const paginationTotalCount = needsFullDataset
    ? sortTotalCount
    : backendTotalCount;

  /** Loading state  *********************************************************
   * only show loading when filters change */

  const [didFiltersChange, setDidFiltersChange] = useState(true);

  useEffect(() => {
    setDidFiltersChange(true);
  }, [search, selectedOwner, selectedShared, urlPage]);

  useEffect(() => {
    if (!isFetching) {
      setDidFiltersChange(false);
    }
  }, [isFetching]);

  // Show loading when full dataset is needed but hasn't arrived yet.
  // Covers both column sort (guarded by isSortEnabled) and merged view.
  const isFullDatasetLoading =
    !hasFullDataset && (isMergedView || (!!sortState.field && isSortEnabled));

  return {
    apps,
    isFetching: isFetching && didFiltersChange,
    isLoading: isUseAppsLoading || isUserLoading || isFullDatasetLoading,
    isSortEnabled,
    isSortDisabledDueToLimit,
    onSortChange,
    paginationPage,
    paginationTotalCount,
    paginationTotalPages,
    sortDescriptor,
  };
}
